// React Query hooks da Agenda > Estoque — chaves próprias, mesmo padrão de
// hooks/queries/chips.ts.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  gpsDriveStockService,
  type CreateStockItemsInput,
  type MoveTrackerInput,
  type UpdateStockItemInput,
} from "@/services/gpsdrive-stock.service";

const modelsKey = ["stock", "models"] as const;
const itemsKey = ["stock", "items"] as const;

export const useStockModels = () =>
  useQuery({ queryKey: modelsKey, queryFn: gpsDriveStockService.models, retry: false });

export const useStockItems = () =>
  useQuery({ queryKey: itemsKey, queryFn: gpsDriveStockService.items, retry: false });

export const useFleetTrackers = () =>
  useQuery({
    queryKey: ["stock", "fleet"] as const,
    queryFn: gpsDriveStockService.fleetTrackers,
    retry: false,
  });

export function useSaveModel() {
  const qc = useQueryClient();
  return useMutation({
    // Sobe a foto (se houver) e cria/atualiza o modelo numa tacada.
    mutationFn: async (v: { id?: string; nome: string; file?: File | null }) => {
      const imageKey = v.file ? (await gpsDriveStockService.uploadImage(v.file)).key : undefined;
      return v.id
        ? gpsDriveStockService.updateModel(v.id, { nome: v.nome, imageKey })
        : gpsDriveStockService.createModel({ nome: v.nome, imageKey });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: modelsKey }),
  });
}

export function useCreateStockItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStockItemsInput) => gpsDriveStockService.createItems(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });
}

export function useUpdateStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateStockItemInput }) =>
      gpsDriveStockService.updateItem(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });
}

export function useDiscardStockItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gpsDriveStockService.discardItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: itemsKey }),
  });
}

export const useStockMovements = () =>
  useQuery({
    queryKey: ["stock", "movements"] as const,
    queryFn: gpsDriveStockService.movements,
    retry: false,
  });

export function useMoveTracker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MoveTrackerInput) => gpsDriveStockService.moveTracker(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stock"] }),
  });
}
